import { IObjectTranslatable, RelationalActionTranslator, TextAlignKindsTranslator, HtmlColorCodeTranslator } from ".";
import { IRichTextBoxItemBean, ITypeDefinable, BeanTypes } from "../beans/items";
import { IItem, RichTextBoxItem } from "../../../../domain/document/items";

import {
  Point,
  Size,
  Rectangle,
  Color,
  TextStyle,
} from "../../../../domain/drawing";
import { RelationalActionExtractor } from "./RelationalActionExtractor";
import { RichTextBoxInputMethodOptionKinds } from "../../../../domain/document/inputMethodOptionKinds";
import { RichTextBoxInputMethodKinds } from "../../../../domain/document/inputMethodKinds";

export class RichTextBoxItemTranslator implements IObjectTranslatable {
  toItem(item: ITypeDefinable, formatId: number, groupId: number, documentId: number, executionMode: string): IItem {
    const bean = item as IRichTextBoxItemBean;
    const result = new RichTextBoxItem(bean.richTextAsHtml);
    result.executionMode = executionMode;
    result.privateId = (bean as any).privateId; // フロントエンド独自パラメータ.
    result.uniqueKey = bean.uniqueKey;
    result.id = bean.id;
    result.sectionObjectUid = bean.sectionObjectUid;
    result.isEditable = true; // TODO:特に問題なければプロパティを削除する
    result.isAdditional = !!bean.isAdditional;
    result.isDeleted = !!bean.isDeleted;
    result.isVisible = !!bean.isVisible;
    result.typeDetail = bean.typeDetail;
    result.viewTitle = bean.viewTitle;
    result.printBehavior.shouldPrint = !!bean.printVisible;
    result.printBehavior.shouldPreview = !!bean.previewVisible;
    result.name = bean.name;
    result.actions = RelationalActionTranslator.toItems(bean.relationalItems);
    const adjustedHeight = bean.adjustedHeight ? bean.adjustedHeight : bean.height;
    result.geometry.rectangle = new Rectangle(new Point(bean.x, bean.y), new Size(bean.width, adjustedHeight));
    result.textStyle = new TextStyle(new Color(bean.textColor), TextAlignKindsTranslator.toKind(bean.textAlign), bean.textFontSize, undefined, bean.textFontName, bean.textLineHeight, true);
    result.imeMode = bean.defaultIME;
    result.isFreeInputable = !!bean.supportedFreeText;
    result.inputMethodOptionKind = RichTextBoxInputMethodOptionKinds.toInputMethodOptionKinds(bean);
    result.inputMethodKind = this.toInputMethodKind(bean, result.inputMethodOptionKind, documentId != null);
    result.enableSingleEmptyCheck = bean.emptyCheck;
    result.enableEmptyCheckConfirmation = bean.isEmptyCheckIgnorable;
    result.alertGroupKey = bean.alertGroup;
    result.toolTip = bean.toolTip;
    result.enableLengthCheck = !!bean.lengthCheck;
    result.lengthChecklength = bean.length;
    result.parentAlertInformations = RelationalActionExtractor.extractParentAlertParameters(result.actions);
    result.shouldQuote = !!bean.isQuotable;
    result.supportedQuotable = !!bean.supportedQuotable;
    result.paintStyle = HtmlColorCodeTranslator.toPaintStyleFill(bean.backColor);
    result.paintStyleOnEditing = bean.isDisplayBackColorSpecified
      ? HtmlColorCodeTranslator.toPaintStyleFill(bean.displayBackColor)
      : null;
    result.originalHeight = bean.height;
    result.isRelayoutableByHeight = bean.isChangeHeight;
    result.pasteDestinations = RelationalActionExtractor.extractTextPasteParameters(result.actions);
    result.scriptExecuteParameters = RelationalActionExtractor.extractScriptExecuteParameters(result.actions);
    result.dacsProcessType = bean.dacsProcessType;
    result.dacsItemName = bean.dacsItemName;
    result.dacsItemCode = bean.dacsItemCode;
    result.mksTemplateFormats = RelationalActionExtractor.extractMksTemplateFormats(result.actions);
    result.mksTemplateRegisteredId = bean.mksTemplateRegisteredId;
    result.dynamicTemplateFormats = RelationalActionExtractor.extractDynamicTemplateFormats(result.actions);
    result.dynamicTemplateRegisteredId = bean.dynamicTemplateRegisteredId;
    result.isLineSpacingNarrowForLogicalFont = !!bean.isLineSpacingNarrowForLogicalFont;
    return result;
  }

  private toInputMethodKind(bean: IRichTextBoxItemBean, optionKind: RichTextBoxInputMethodOptionKinds, isRegistered: boolean): RichTextBoxInputMethodKinds {
    // MKS・DYNAMICテンプレートの指定になっていても、V1フォーマットの時点でフリー入力フラグがtrueになってしまっているフォーマットがあるので対策している
    if (bean.enabledFreeText) {
      if (optionKind === RichTextBoxInputMethodOptionKinds.MKSTemplate && !isRegistered) {
        // 救済必要なデータなのでMKSテンプレート入力にする
        return RichTextBoxInputMethodKinds.MKSTemplate;
      }
      if (optionKind === RichTextBoxInputMethodOptionKinds.DynamicTemplate && !isRegistered) {
        // 救済必要なデータなのでDynamicテンプレート入力にする
        return RichTextBoxInputMethodKinds.DynamicTemplate;
      }
      return RichTextBoxInputMethodKinds.FreeInput;
    }
    return RichTextBoxInputMethodOptionKinds.getDefualtInputMethodKind(optionKind);
  }

  toBean(item: IItem): ITypeDefinable {
    const target = item as RichTextBoxItem;
    const bean: IRichTextBoxItemBean = {
      type: BeanTypes.RichText,
      uniqueKey: target.uniqueKey,
      id: target.id,
      sectionObjectUid: target.sectionObjectUid,
      isVisible: target.isVisible,
      printVisible: target.printBehavior.shouldPrint,
      previewVisible: target.printBehavior.shouldPreview,
      x: target.geometry.rectangle.x,
      y: target.geometry.rectangle.y,
      width: target.geometry.rectangle.width,
      adjustedHeight: target.geometry.rectangle.height,
      height: target.originalHeight,
      isAdditional: target.isAdditional,
      isDeleted: target.isDeleted,
      name: target.name,
      textColor: HtmlColorCodeTranslator.fromColor(target.textStyle.color),
      textFontName: target.textStyle.fontFamily,
      textFontSize: target.textStyle.baseFontSize,
      textLineHeight: target.textStyle.lineHeight,
      textAlign: TextAlignKindsTranslator.fromKind(target.textStyle.textAlign),
      text: target.plainText, // TODO:返却する必要ある？
      defaultIME: target.imeMode,
      supportedFreeText: target.isFreeInputable,
      richTextAsHtml: target.value,
      relationalItems: null,  // TODO:返却する必要ある？
      emptyCheck: target.enableSingleEmptyCheck,
      isEmptyCheckIgnorable: target.enableEmptyCheckConfirmation,
      alertGroup: target.alertGroupKey,
      toolTip: target.toolTip,
      lengthCheck: target.enableLengthCheck,
      length: target.lengthChecklength,
      enabledFreeText: target.shouldInputFree,  // バックエンド側が対応未了。入力方法切替UIを実装したら保存させる対応が必要
      isQuotable: target.shouldQuote,
      supportedQuotable: target.supportedQuotable,
      backColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyle),
      displayBackColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyleOnEditing),
      isDisplayBackColorSpecified: !!target.paintStyleOnEditing,
      isChangeHeight: target.isRelayoutableByHeight,
      typeDetail: target.typeDetail,
      viewTitle: target.viewTitle,
      dacsProcessType: target.dacsProcessType,
      dacsItemName: target.dacsItemName,
      dacsItemCode: target.dacsItemCode,
      mksTemplateRegisteredId: target.mksTemplateRegisteredId,
      dynamicTemplateRegisteredId: target.dynamicTemplateRegisteredId,
      isLineSpacingNarrowForLogicalFont: target.isLineSpacingNarrowForLogicalFont,
    };
    return bean;
  }
}
